import { type ReactNode, useState } from 'react';
import type { FacetsResponse } from '@/api/client';
import { compactCount, formatCount } from '@/lib/format';
import { hasField, termState } from '@/lib/terms';
import { Icon } from './Icon';

const GROUPS: ReadonlyArray<[string, string]> = [
    ['level', 'Level'],
    ['service', 'Service'],
    ['kind', 'Kind'],
    ['module', 'Module'],
    ['logger', 'Logger'],
    ['exception', 'Exception'],
    ['method', 'Method'],
    ['status', 'HTTP status'],
    ['user', 'User'],
    ['instance', 'Instance'],
    ['commit', 'Commit'],
];
const LEVEL_COLOUR: Readonly<Record<string, string>> = {
    error: 'var(--critical-ink)',
    fatal: 'var(--critical-ink)',
    warn: 'var(--warning)',
    info: 'var(--data)',
};
const LEVEL_ORDER = ['fatal', 'error', 'warn', 'info', 'debug', 'trace'];
const COLLAPSED_KEY = 'fl.facets.collapsed';

function loadCollapsed(): Set<string> {
    try {
        return new Set(JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '["instance","commit"]') as string[]);
    } catch {
        return new Set(['instance', 'commit']);
    }
}

export interface FacetsProps {
    facets: FacetsResponse | undefined;
    q: string;
    privacy: boolean;
    loading: boolean;
    onTerm: (field: string, value: string, exclude: boolean) => void;
    onRemove: (field: string, value: string) => void;
    onClear: (field: string) => void;
}

// What the current query holds, counted by field: a click keeps a value (again: lets it go), − excludes it.
export function Facets(props: FacetsProps): ReactNode {
    const [collapsed, setCollapsed] = useState(loadCollapsed);
    const total = props.facets?.level?.reduce((sum, item) => sum + item.count, 0) ?? 0;
    const toggle = (field: string): void => {
        setCollapsed((previous) => {
            const next = new Set(previous);
            if (!next.delete(field)) {
                next.add(field);
            }
            try {
                localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
            } catch {
                // Remembered for this page only.
            }

            return next;
        });
    };

    return (
        <aside className={`facets ${props.loading ? 'loading' : ''}`}>
            <div className="facets-body">
                {GROUPS.map(([field, title]) => {
                    const values = [...(props.facets?.[field] ?? [])];
                    const filtered = hasField(props.q, field);
                    if (values.length === 0 && !filtered) {
                        return null;
                    }
                    if (field === 'level') {
                        values.sort((a, b) => LEVEL_ORDER.indexOf(a.value) - LEVEL_ORDER.indexOf(b.value));
                    }
                    const max = Math.max(1, ...values.map((item) => item.count));
                    const open = !collapsed.has(field);

                    return (
                        <div key={field} className={`facet ${open ? '' : 'closed'}`}>
                            <h4>
                                <button className="fh" onClick={() => toggle(field)} title={open ? 'Fold' : 'Unfold'}>
                                    <span className="caret">
                                        <Icon name="chevron" size={12} />
                                    </span>
                                    {title}
                                    <span className="nv">{values.length}</span>
                                </button>
                                {filtered && (
                                    <button className="fclear" title={`Remove every ${field} filter`} onClick={() => props.onClear(field)}>
                                        Clear
                                    </button>
                                )}
                            </h4>
                            {open &&
                                values.map(({ value, count }) => {
                                    const state = termState(props.q, field, value);
                                    const label = field === 'commit' ? value.slice(0, 9) : value;

                                    return (
                                        <div key={value} className={`fv ${state ?? ''}`}>
                                            <button
                                                className="fk"
                                                title={state ? 'Click: remove this filter' : `Keep ${field}:${value}`}
                                                onClick={(event) => (state ? props.onRemove(field, value) : props.onTerm(field, value, event.altKey))}
                                            >
                                                <span className="n">
                                                    {field === 'level' && (
                                                        <span className="lvdot" style={{ background: LEVEL_COLOUR[value] ?? 'var(--ink-ghost)' }} />
                                                    )}
                                                    {field === 'user' && props.privacy ? (
                                                        <span className="redact">████████</span>
                                                    ) : (
                                                        <span className={field === 'status' ? statusClass(value) : ''}>{label}</span>
                                                    )}
                                                </span>
                                                <span className="meter">
                                                    <b style={{ width: `${Math.max(3, (count / max) * 100)}%` }} />
                                                </span>
                                                <span className="c" title={formatCount(count)}>
                                                    {compactCount(count)}
                                                </span>
                                            </button>
                                            <span className="fa">
                                                <button title={`Keep ${field}:${value}`} onClick={() => props.onTerm(field, value, false)}>
                                                    <Icon name="plus" size={13} />
                                                </button>
                                                <button title={`Exclude ${field}:${value}`} onClick={() => props.onTerm(field, value, true)}>
                                                    <Icon name="minus" size={13} />
                                                </button>
                                            </span>
                                        </div>
                                    );
                                })}
                        </div>
                    );
                })}
                {props.facets && total === 0 && <div className="facets-empty micro">Nothing to count</div>}
            </div>
        </aside>
    );
}

const statusClass = (value: string): string => (value.startsWith('5') ? 's5' : value.startsWith('4') ? 's4' : '');
